import { createAsyncThunk } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';

import { AuthError } from '@auspost/auth0-uiim-types';

import { errorContent } from 'App/common/Notification/Notification.config';
import { apiEndpoint, APP_REQUEST_CONFIGS } from 'config/track-config';
import { Notification, NotificationStatus, NotificationType } from 'models/Notification';
import { AppAsyncThunkOpts } from 'models/Store';
import { deleteSession } from 'store/customer';
import { createNotification } from 'store/notification';
import { AppDispatch } from 'store/store';
import axiosInstance from 'utils/axios.utils';

import { clearShipmentList } from '../list/shipmentList.slice';

export const saveNickname = createAsyncThunk<
  string | undefined,
  { trackingId: string; nickname?: string },
  AppAsyncThunkOpts
>('nickname/saveNickname', async ({ trackingId, nickname }, thunkAPI) => {
  const { dispatch } = thunkAPI;
  try {
    const response = await apiPatchNickname(trackingId, nickname);
    const { status, data } = response;
    if (status !== 200) return;
    const newNickname = data.nickname;
    return newNickname;
  } catch (error) {
    const authError = error as AuthError;
    if (
      (authError.isReauthRequiredError && authError.isReauthRequiredError()) ||
      (authError.isLoginRequiredError && authError.isLoginRequiredError())
    ) {
      const status = 401;
      if (status) handleNicknameError(dispatch, status);
      return thunkAPI.rejectWithValue({
        status,
        data: authError.message,
        isAxiosError: false,
      });
    }

    if (axios.isAxiosError(error)) {
      const axiosErr = error as AxiosError;
      const status = axiosErr.response?.status;
      if (status) handleNicknameError(dispatch, status);
      return thunkAPI.rejectWithValue({
        status,
        data: axiosErr.response?.data,
        isAxiosError: axiosErr.isAxiosError,
      });
    }

    throw error;
  }
});

/**
 * Makes an API call to patch shipment from Shipments Gateway API to update or remove the nickname field
 * @param trackingId The tracking ID of the shipment to get
 * @param nickname Replace or remove the nickname that is attached to tracking ID
 */
const apiPatchNickname = (trackingId: string, nickname?: string) => {
  const _shipmentUrl = `${apiEndpoint.shipmentsV1}/${trackingId}`;
  const operation = !!nickname ? 'replace' : 'remove';
  const requestConfig = APP_REQUEST_CONFIGS.shipment;
  requestConfig.headers.AP_CHANNEL_NAME = 'details';
  const body = [
    {
      op: operation,
      path: '/nickname',
      value: nickname,
    },
    {
      op: operation,
      path: '/articles/-/nickname',
      value: nickname,
    },
  ];

  return axiosInstance.patch(_shipmentUrl, body, requestConfig);
};

const handleNicknameError = (dispatch: AppDispatch, status: number) => {
  const notification: Notification = {
    type: NotificationType.ALERT,
    status: NotificationStatus.ERROR,
    message: errorContent.nicknameError,
  };
  if (status === 401) {
    notification.status = NotificationStatus.INFO;
    notification.message = errorContent.expiredSession;
    dispatch(clearShipmentList());
    dispatch(deleteSession());
    return dispatch(createNotification(notification));
  }
  return dispatch(createNotification(notification));
};
